import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaBoxOpen,
  FaBuilding,
  FaCalendarAlt,
  FaCalendarCheck,
  FaCheckCircle,
  FaClipboardList,
  FaClock,
  FaEllipsisV,
  FaEye,
  FaIndustry,
  FaInbox,
  FaPaperPlane,
  FaRedo,
  FaRoute,
  FaSearch,
  FaStar,
  FaTable,
  FaThLarge,
  FaTruck,
  FaTruckLoading,
  FaUser,
  FaWallet,
} from "react-icons/fa";
import { toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import { LOGISTICS_TRANSPORT_MODES } from "../../constants/logisticsProfileFormOptions";
import { buildLogisticsViewRfqPathFromIds } from "../website/logisticsRfqSubmissionStorage";
import { legacyPreferredPaymentTermsObjectToLabels } from "../website/logisticsGenericRfqFormState";
import { useCurrencyMaster } from "../../hooks/useCurrencyMaster";
import { getAvailableRoles } from "../../utils/roleSwitcher";
import "../../assets/styles/logistics_provider_rfqs_received.scss";
import "../../assets/styles/logistics_generate_invoice.scss";
import "../../assets/styles/grn_manifest_list.scss";
import { useFactoryDetailsMaster } from "../../hooks/useFactoryDetailsMaster";
import ShippingManifestDetailsModal from "../../components/ShippingManifestDetailsModal";
import LogisticsGrnDetailsModal from "../../components/LogisticsGrnDetailsModal";
import LogisticsGrnAgrlAcceptModal from "../../components/LogisticsGrnAgrlAcceptModal";
import { transformManifestData } from "../../utils/shippingManifestView";
import { isLogisticsGrnAcceptStageComplete } from "../../utils/logisticsRfqGrnGate";
import { isLogisticsStageProgressDone } from "../../utils/logisticsRfqSentDeliveryUi";
import { fetchLogisticsProviderDisplayInfo } from "../../utils/logisticsProviderContactDisplay";

const PAGE_SIZE = 9;
const LPRFQ_VIEW_STORAGE_KEY = "lprfq_received_view_format";

const RFQ_KIND_LABELS = {
  generic: "Generic logistics",
  "business-relocation": "Business relocation",
  "exhibition-logistics": "Exhibition logistics",
};

const RELOCATION_SCOPE_OPTIONS = [
  { value: "office", label: "Office Relocation" },
  { value: "employee", label: "Employee Relocation" },
  { value: "both", label: "Both Office & Employee" },
];

function relocationScopeLabel(scope) {
  const s = scope != null ? String(scope).trim().toLowerCase() : "";
  const o = RELOCATION_SCOPE_OPTIONS.find((x) => x.value === s);
  return o ? o.label : scope ? String(scope) : null;
}

function transportModeLabel(modeId) {
  if (modeId == null || String(modeId).trim() === "") return null;
  const id = String(modeId).trim().toLowerCase();
  const m = LOGISTICS_TRANSPORT_MODES.find((x) => x.id === id);
  return m ? m.label : String(modeId);
}

function rfqKindLabel(kind) {
  const k = kind != null ? String(kind).trim() : "";
  if (!k) return "—";
  return RFQ_KIND_LABELS[k] || k;
}

function formatDate(dateString) {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "N/A";
  }
}

function formatExpoDate(v) {
  if (v == null || String(v).trim() === "") return "—";
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    try {
      const d = new Date(`${s}T12:00:00`);
      if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
    } catch {
      /* fall through */
    }
  }
  try {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
  } catch {
    /* ignore */
  }
  return s;
}

/** API / DB sometimes returns JSON columns as serialized strings in the list payload. */
function parseJsonObjectIfString(v) {
  if (v != null && typeof v === "object" && !Array.isArray(v)) return v;
  if (typeof v === "string") {
    const t = v.trim();
    if (!t) return null;
    try {
      const j = JSON.parse(t);
      return j != null && typeof j === "object" && !Array.isArray(j) ? j : null;
    } catch {
      return null;
    }
  }
  return null;
}

function parseJsonArrayIfString(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === "string") {
    const t = v.trim();
    if (!t) return null;
    try {
      const j = JSON.parse(t);
      return Array.isArray(j) ? j : null;
    } catch {
      return null;
    }
  }
  return null;
}

/** Same rules as POST accept-grn (manifest + GRN waivers when progress lags). */
function canCompleteLogisticsGrnAcceptPipeline(dp, progParsed, ctx) {
  const hasGrn = Boolean(ctx?.hasGrn);
  const manifestAccepted = Boolean(ctx?.manifestAccepted);
  const hasShippingManifest = Boolean(ctx?.hasShippingManifest);
  if (!dp || !Array.isArray(dp.selectedStages) || !dp.selectedStages.length) return false;
  const order = dp.selectedStages.map((s) => String(s || "").trim()).filter(Boolean);
  const accIdx = order.findIndex((k) => k.toLowerCase() === "grn_accept");
  if (accIdx < 0) return false;
  const grnKey = order[accIdx];
  const grnVal = progParsed[grnKey] ?? progParsed.grn_accept ?? progParsed.grnAccept;
  if (isLogisticsStageProgressDone(grnVal)) return false;
  for (let i = 0; i < accIdx; i += 1) {
    const k = order[i];
    if (isLogisticsStageProgressDone(progParsed[k])) continue;
    const kl = k.toLowerCase();
    if (!hasShippingManifest) return false;
    if (kl === "manifest_accept" && manifestAccepted) continue;
    if (kl === "grn_generate" && hasGrn) continue;
    if (kl === "transit" && hasGrn) continue;
    if ((kl === "eway_generate" || kl === "eway_track" || kl === "manifest_create") && manifestAccepted) continue;
    return false;
  }
  return true;
}

/**
 * Pickup done, manifest accepted, GRN exists, delivery plan includes `grn_accept`, and pipeline is
 * ready for logistics to complete the GRN acceptance step (same rule as POST accept-grn).
 */
function shouldShowLogisticsAcceptGrnButton(rfqRow, grnEntity) {
  if (!rfqRow || typeof rfqRow !== "object") return false;
  const ps = String(rfqRow.handoverPickupStatus || "").trim().toLowerCase();
  const pickupOk = ps === "picked_up" || ps === "picked up" || ps === "completed";
  const smId = rfqRow.shippingManifestId != null ? Number(rfqRow.shippingManifestId) : NaN;
  const hasManifest = Number.isFinite(smId) && smId > 0;
  const rowGrn = rfqRow.logisticsGrnId != null ? Number(rfqRow.logisticsGrnId) : NaN;
  const grnFromRow = Number.isFinite(rowGrn) && rowGrn > 0 ? rowGrn : null;
  const gid =
    grnEntity?.id != null && Number.isFinite(Number(grnEntity.id)) && Number(grnEntity.id) > 0
      ? Number(grnEntity.id)
      : grnEntity?.grn_id != null && Number.isFinite(Number(grnEntity.grn_id)) && Number(grnEntity.grn_id) > 0
        ? Number(grnEntity.grn_id)
        : null;
  const hasGrn = grnFromRow != null || gid != null;

  const dp = rfqRow.deliveryProcess;
  if (!dp || !Array.isArray(dp.selectedStages) || dp.selectedStages.length === 0) return false;

  const stagesLower = dp.selectedStages.map((s) => String(s || "").trim().toLowerCase());
  if (!stagesLower.includes("grn_accept")) return false;

  const progRaw = dp?.stageProgress;
  const progParsed =
    progRaw != null && typeof progRaw === "object" && !Array.isArray(progRaw)
      ? progRaw
      : parseJsonObjectIfString(progRaw) || {};
  const manifestAccepted =
    Boolean(rfqRow.shippingManifestAccepted) ||
    isLogisticsStageProgressDone(progParsed.manifest_accept ?? progParsed.manifestAccept);

  if (!pickupOk || !hasManifest || !manifestAccepted || !hasGrn) return false;

  if (
    !canCompleteLogisticsGrnAcceptPipeline(dp, progParsed, {
      hasGrn,
      manifestAccepted,
      hasShippingManifest: hasManifest,
    })
  )
    return false;

  return true;
}

function RfqMetaRow({ label, value, children }) {
  return (
    <div className="lprfq-detail-row">
      <span className="lprfq-detail-row__icon" aria-hidden>
        {children}
      </span>
      <div className="lprfq-detail-row__text">
        <span className="lprfq-detail-row__label">{label}</span>
        <span className="lprfq-detail-row__value">{value ?? "—"}</span>
      </div>
    </div>
  );
}

function getKindTitleClass(kind) {
  const k = kind != null ? String(kind).trim() : "";
  switch (k) {
    case "generic":
      return "lprfq-card__category lprfq-card__category--generic";
    case "business-relocation":
      return "lprfq-card__category lprfq-card__category--relocation";
    case "exhibition-logistics":
      return "lprfq-card__category lprfq-card__category--exhibition";
    default:
      return "lprfq-card__category";
  }
}

function getRowLifecycleKey(row) {
  const ps = String(row.handoverPickupStatus || "").trim().toLowerCase();
  const qs = normalizeProviderQuoteStatus(row.quoteStatus);
  if (qs === "accepted" && isLogisticsGrnAcceptStageComplete(row)) return "completed";
  if (ps === "picked_up" || ps === "picked up" || ps === "completed") return "picked_up";
  if (qs === "submitted" || qs === "accepted" || qs === "pending_approval" || qs === "rejected") {
    return "quoted";
  }
  return "pending_quote";
}

function getCardStatusPill(row) {
  const key = getRowLifecycleKey(row);
  if (key === "completed") return { label: "Completed", className: "lprfq-pill lprfq-pill--completed" };
  if (key === "picked_up") return { label: "Picked Up", className: "lprfq-pill lprfq-pill--picked" };
  if (key === "quoted") {
    const qs = normalizeProviderQuoteStatus(row.quoteStatus);
    if (qs === "accepted") return { label: "Accepted", className: "lprfq-pill lprfq-pill--accepted" };
    if (qs === "rejected") return { label: "Rejected", className: "lprfq-pill lprfq-pill--pending" };
    if (qs === "pending_approval") return { label: "Quoted", className: "lprfq-pill lprfq-pill--quoted" };
    return { label: "Quoted", className: "lprfq-pill lprfq-pill--quoted" };
  }
  return { label: "Pending Quote", className: "lprfq-pill lprfq-pill--pending" };
}

function truncateRef(ref, max = 14) {
  const s = ref != null ? String(ref).trim() : "";
  if (s.length <= max) return s;
  return `${s.slice(0, max)}…`;
}

function receivedRowKey(row) {
  const src = String(row?.listSource ?? "logistics_generic").trim();
  return `${src}:${row?.id}-${row?.logisticsProviderCompanyId}`;
}

function getKindTableVisual(kind) {
  const k = kind != null ? String(kind).trim() : "";
  switch (k) {
    case "business-relocation":
      return { tone: "relocation", Icon: FaBuilding };
    case "exhibition-logistics":
      return { tone: "exhibition", Icon: FaStar };
    default:
      return { tone: "generic", Icon: FaBoxOpen };
  }
}

function receivedRowDetailSummary(r) {
  if (r.rfqKind === "exhibition-logistics") {
    return r.exhibitionName || "—";
  }
  if (r.rfqKind === "business-relocation") {
    return relocationScopeLabel(r.relocationScope) || "—";
  }
  if (r.industry) return r.industry;
  return transportModeLabel(r.transportMode) || "—";
}

function resolveRowLogisticsMode(r) {
  const p = r?.payload && typeof r.payload === "object" ? r.payload : {};
  const raw = p.logisticsMode ?? p.logistics_mode ?? r.transportMode ?? r.transport_mode;
  return raw != null && String(raw).trim() !== "" ? String(raw).trim() : "—";
}

function resolveRowBudgetRange(r) {
  const p = r?.payload && typeof r.payload === "object" ? r.payload : {};
  const raw = p.budgetRange ?? p.budget_range;
  return raw != null && String(raw).trim() !== "" ? String(raw).trim() : "—";
}

function buildReceivedRfqViewPath(r) {
  return buildLogisticsViewRfqPathFromIds(
    r.logisticsProviderCompanyId,
    r.id,
    r.rfqReferenceNumber,
    "logistics-inbox",
    r.rfqKind,
  );
}

function normalizeProviderQuoteStatus(status) {
  return String(status || "").trim().toLowerCase();
}

/** Provider has already submitted a quote (including P2P pending internal approval). */
function isProviderQuoteSubmitted(status) {
  const qs = normalizeProviderQuoteStatus(status);
  return qs === "submitted" || qs === "accepted" || qs === "pending_approval";
}

function computeStats(allRows) {
  let pendingQuote = 0;
  let pickedUp = 0;
  let completed = 0;
  allRows.forEach((r) => {
    const k = getRowLifecycleKey(r);
    if (k === "completed") completed += 1;
    else if (k === "picked_up") pickedUp += 1;
    else if (k === "pending_quote") pendingQuote += 1;
  });
  return {
    total: allRows.length,
    pendingQuote,
    pickedUp,
    completed,
  };
}

function filterRows(allRows, { search, statusFilter, kindFilter, dateFrom, dateTo }) {
  const q = search.trim().toLowerCase();
  return allRows.filter((r) => {
    if (kindFilter !== "all" && String(r.rfqKind) !== kindFilter) return false;
    if (statusFilter !== "all" && getRowLifecycleKey(r) !== statusFilter) return false;
    if (dateFrom || dateTo) {
      const t = r.submittedAt ? new Date(r.submittedAt).getTime() : NaN;
      if (!Number.isFinite(t)) return false;
      if (dateFrom) {
        const from = new Date(`${dateFrom}T00:00:00`).getTime();
        if (t < from) return false;
      }
      if (dateTo) {
        const to = new Date(`${dateTo}T23:59:59`).getTime();
        if (t > to) return false;
      }
    }
    if (!q) return true;
    const hay = [
      r.rfqReferenceNumber,
      r.contactPerson,
      r.industry,
      r.exhibitionName,
      r.relocationScope,
      transportModeLabel(r.transportMode),
      rfqKindLabel(r.rfqKind),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function mapCountriesResponse(response) {
  const apiCountries = Array.isArray(response?.data)
    ? response.data
    : response?.data?.countries || response?.data?.master_countries || [];
  return apiCountries
    .map((country) => {
      const id = country.country_id ?? country.id ?? country.value;
      const label =
        country.country_name ?? country.country_label ?? country.label ?? country.name ?? country.country;
      return { id: id != null ? String(id) : "", label: label != null ? String(label) : "" };
    })
    .filter((c) => c.id && c.label);
}

function mapStatesResponse(response) {
  const apiStates = Array.isArray(response?.data)
    ? response.data
    : response?.data?.states || response?.data?.master_states || [];
  return apiStates
    .map((state) => {
      const id = state.state_id ?? state.id ?? state.value;
      const label = state.state_name ?? state.state_label ?? state.label ?? state.name ?? state.state;
      return { id: id != null ? String(id) : "", label: label != null ? String(label) : "" };
    })
    .filter((s) => s.id && s.label);
}

function mapCitiesResponse(response) {
  const apiCities = Array.isArray(response?.data)
    ? response.data
    : response?.data?.cities || response?.data?.master_cities || [];
  return apiCities
    .map((city) => {
      const id = city.city_id ?? city.id ?? city.value;
      const label = city.city_name ?? city.city_label ?? city.label ?? city.name ?? city.city;
      return { id: id != null ? String(id) : "", label: label != null ? String(label) : "" };
    })
    .filter((c) => c.id && c.label);
}

async function resolveLocationLabels(countryId, stateId, cityId) {
  const cid = countryId != null && String(countryId).trim() !== "" ? String(countryId).trim() : "";
  const sid = stateId != null && String(stateId).trim() !== "" ? String(stateId).trim() : "";
  const cyid = cityId != null && String(cityId).trim() !== "" ? String(cityId).trim() : "";
  if (!cid) {
    return { country: "—", state: "—", city: "—" };
  }
  try {
    const cRes = await CommonService.get("/master/company-Country");
    const countries = mapCountriesResponse(cRes);
    const countryLabel = countries.find((c) => c.id === cid)?.label || cid;

    let stateLabel = "—";
    if (sid) {
      const sRes = await CommonService.get(`/master/company-State/${cid}`);
      const states = mapStatesResponse(sRes);
      stateLabel = states.find((s) => s.id === sid)?.label || sid;
    }

    let cityLabel = "—";
    if (cyid && sid) {
      const cityRes = await CommonService.get(`/master/company-City/${sid}`);
      const cities = mapCitiesResponse(cityRes);
      cityLabel = cities.find((c) => c.id === cyid)?.label || cyid;
    }

    return { country: countryLabel, state: stateLabel, city: cityLabel };
  } catch {
    return {
      country: cid,
      state: sid || "—",
      city: cyid || "—",
    };
  }
}

/** Same response shape as buyer “sent” list with `detail=1`. */
function normalizeRows(payload) {
  const body = payload?.data ?? payload ?? {};
  const nested = body?.data ?? body;
  const raw = Array.isArray(nested?.items) ? nested.items : [];
  return raw
    .map((row) => {
      const id = row?.id != null ? Number(row.id) : NaN;
      const providerCid = Number(
        row.logisticsProviderCompanyId ?? row.logistics_provider_company_id ?? NaN,
      );
      const ref = row.rfqReferenceNumber ?? row.rfq_reference_number;
      const refStr = ref != null ? String(ref).trim() : "";
      const contactPerson =
        row.contactPerson ??
        row.contact_person ??
        (row.contactPersonName != null ? row.contactPersonName : null);
      const contactStr =
        contactPerson != null && String(contactPerson).trim() !== ""
          ? String(contactPerson).trim()
          : "—";
      const rfqKind = row.rfqKind ?? row.rfq_kind ?? "";
      const submittedAt = row.submittedAt ?? row.submitted_at ?? null;
      const relocationScopeRaw = row.relocationScope ?? row.relocation_scope ?? null;
      const relocationScope =
        relocationScopeRaw != null && String(relocationScopeRaw).trim() !== ""
          ? String(relocationScopeRaw).trim()
          : null;
      const industryRaw = row.industry ?? null;
      const industry =
        industryRaw != null && String(industryRaw).trim() !== "" ? String(industryRaw).trim() : null;
      const transportModeRaw = row.transportMode ?? row.transport_mode ?? null;
      const transportMode =
        transportModeRaw != null && String(transportModeRaw).trim() !== ""
          ? String(transportModeRaw).trim()
          : null;
      const exhibitionNameRaw = row.exhibitionName ?? row.exhibition_name ?? null;
      const exhibitionName =
        exhibitionNameRaw != null && String(exhibitionNameRaw).trim() !== ""
          ? String(exhibitionNameRaw).trim()
          : null;
      const exhibitionStartRaw = row.exhibitionStartDate ?? row.exhibition_start_date ?? null;
      const exhibitionStartDate =
        exhibitionStartRaw != null && String(exhibitionStartRaw).trim() !== ""
          ? String(exhibitionStartRaw).trim()
          : null;
      const exhibitionEndRaw = row.exhibitionEndDate ?? row.exhibition_end_date ?? null;
      const exhibitionEndDate =
        exhibitionEndRaw != null && String(exhibitionEndRaw).trim() !== ""
          ? String(exhibitionEndRaw).trim()
          : null;
      const payloadJson = row.payload ?? row.payload_json ?? null;
      const listSource = row.listSource ?? row.list_source ?? null;
      const allocator = row.allocator && typeof row.allocator === 'object' ? row.allocator : null;
      const providerAssignmentStatus =
        row.providerAssignmentStatus ?? row.provider_assignment_status ?? null;
      const quoteStatus = row.quoteStatus ?? row.quote_status ?? null;
      const quoteIdRaw = row.quoteId ?? row.quote_id ?? null;
      const handoverIdRaw = row.handoverId ?? row.handover_id ?? null;
      const handoverPickupStatus = row.handoverPickupStatus ?? row.handover_pickup_status ?? null;
      const ewayBillIdRaw = row.ewayBillId ?? row.eway_bill_id ?? null;
      const ewayBillId =
        ewayBillIdRaw != null && String(ewayBillIdRaw).trim() !== "" && Number.isFinite(Number(ewayBillIdRaw))
          ? Number(ewayBillIdRaw)
          : null;
      const shippingManifestIdRaw = row.shippingManifestId ?? row.shipping_manifest_id ?? null;
      const shippingManifestId =
        shippingManifestIdRaw != null &&
        String(shippingManifestIdRaw).trim() !== "" &&
        Number.isFinite(Number(shippingManifestIdRaw))
          ? Number(shippingManifestIdRaw)
          : null;
      const deliveryProcessRaw = row.deliveryProcess ?? row.delivery_process ?? null;
      const rawStages =
        deliveryProcessRaw?.selectedStages ?? deliveryProcessRaw?.selected_stages ?? null;
      const rawProgress =
        deliveryProcessRaw?.stageProgress ?? deliveryProcessRaw?.stage_progress ?? null;
      const rawCurrentKey =
        deliveryProcessRaw?.currentStageKey ?? deliveryProcessRaw?.current_stage_key ?? null;
      const stagesArr = parseJsonArrayIfString(rawStages) ?? (Array.isArray(rawStages) ? rawStages : []);
      const progressObj =
        parseJsonObjectIfString(rawProgress) ??
        (rawProgress && typeof rawProgress === "object" && !Array.isArray(rawProgress) ? rawProgress : {});
      const deliveryProcess =
        deliveryProcessRaw != null &&
        typeof deliveryProcessRaw === "object" &&
        !Array.isArray(deliveryProcessRaw)
          ? {
              selectedStages: stagesArr.map((x) => String(x)),
              stageProgress: progressObj,
              currentStageKey: rawCurrentKey != null ? String(rawCurrentKey).trim() : null,
            }
          : null;
      const smAccRaw = row.shippingManifestAccepted ?? row.shipping_manifest_accepted ?? null;
      const manifestAcceptStageDone = isLogisticsStageProgressDone(
        deliveryProcess?.stageProgress?.manifest_accept,
      );
      const shippingManifestAccepted =
        smAccRaw === true ||
        smAccRaw === 1 ||
        String(smAccRaw || "").trim().toLowerCase() === "true" ||
        manifestAcceptStageDone;
      const logisticsGrnIdRaw = row.logisticsGrnId ?? row.logistics_grn_id ?? null;
      const logisticsGrnId =
        logisticsGrnIdRaw != null &&
        String(logisticsGrnIdRaw).trim() !== "" &&
        Number.isFinite(Number(logisticsGrnIdRaw))
          ? Number(logisticsGrnIdRaw)
          : null;
      const providerInvoiceIdRaw =
        row.providerInvoiceId ?? row.logistics_provider_invoice_id ?? row.provider_invoice_id ?? null;
      const providerInvoiceId =
        providerInvoiceIdRaw != null &&
        String(providerInvoiceIdRaw).trim() !== "" &&
        Number.isFinite(Number(providerInvoiceIdRaw))
          ? Number(providerInvoiceIdRaw)
          : null;
      if (!Number.isFinite(id) || id <= 0 || !Number.isFinite(providerCid) || providerCid <= 0 || !refStr) {
        return null;
      }
      return {
        id,
        logisticsProviderCompanyId: providerCid,
        rfqReferenceNumber: refStr,
        contactPerson: contactStr,
        rfqKind: String(rfqKind || "").trim(),
        submittedAt,
        relocationScope,
        industry,
        transportMode,
        exhibitionName,
        exhibitionStartDate,
        exhibitionEndDate,
        payload: payloadJson && typeof payloadJson === "object" ? payloadJson : null,
        listSource: listSource != null ? String(listSource) : null,
        allocator,
        providerAssignmentStatus:
          providerAssignmentStatus != null ? String(providerAssignmentStatus) : null,
        providerAssignedAt: row.providerAssignedAt ?? row.provider_assigned_at ?? null,
        quoteStatus: quoteStatus != null && String(quoteStatus).trim() !== "" ? String(quoteStatus).trim() : null,
        quoteId:
          quoteIdRaw != null && String(quoteIdRaw).trim() !== "" && Number.isFinite(Number(quoteIdRaw))
            ? Number(quoteIdRaw)
            : null,
        handoverId:
          handoverIdRaw != null && Number.isFinite(Number(handoverIdRaw)) ? Number(handoverIdRaw) : null,
        handoverPickupStatus:
          handoverPickupStatus != null && String(handoverPickupStatus).trim() !== ""
            ? String(handoverPickupStatus).trim()
            : null,
        ewayBillId,
        shippingManifestId,
        shippingManifestAccepted,
        deliveryProcess,
        logisticsGrnId,
        providerInvoiceId,
      };
    })
    .filter(Boolean);
}

function rfqSummaryFromPayload(r) {
  const p = r?.payload && typeof r.payload === "object" ? r.payload : {};
  const kind = r?.rfqKind != null ? String(r.rfqKind).trim().toLowerCase() : "generic";

  const joinLoc = (city, state, country) => {
    const parts = [city, state, country].map((x) => (x != null ? String(x).trim() : "")).filter(Boolean);
    return parts.length ? parts.join(", ") : "—";
  };

  const weight =
    p.totalWeightValue != null || p.totalWeightUnit != null
      ? `${p.totalWeightValue || "—"} ${p.totalWeightUnit || ""}`.trim()
      : p.totalWeightKg
        ? `${p.totalWeightKg} kg`
        : "—";

  let volume =
    p.totalVolumeValue != null || p.totalVolumeUnit != null
      ? `${p.totalVolumeValue || "—"} ${p.totalVolumeUnit || ""}`.trim()
      : p.totalVolumeCbm
        ? `${p.totalVolumeCbm} CBM`
        : "—";

  // Default labels (generic)
  const labels = {
    origin: "Origin",
    destination: "Destination",
    cargoType: "Cargo Type",
    container: "Container",
    weight: "Weight",
    volume: "Volume",
    incoterm: "Incoterms",
    expectedDate: "Expected Date",
    requirements: "Requirements",
  };

  // Default values
  let origin = "—";
  let destination = "—";
  let cargoType = "—";
  let container = "—";
  let incoterm = "—";
  let expectedDate = "—";
  let requirements = "—";
  const preferredPaymentTerms = (() => {
    const raw = p.preferredPaymentTerms;
    if (Array.isArray(raw)) {
      const s = raw.map((x) => String(x).trim()).filter(Boolean);
      return s.length ? s.join(", ") : null;
    }
    if (raw && typeof raw === "object") {
      const labels = legacyPreferredPaymentTermsObjectToLabels(raw);
      return labels.length ? labels.join(", ") : null;
    }
    return null;
  })();

  if (kind === "business-relocation") {
    labels.origin = "Current location";
    labels.destination = "Destination";
    labels.cargoType = "Service";
    labels.container = "Type of Relocation";
    labels.incoterm = "Logistics mode";
    labels.expectedDate = "Preferred move date";
    labels.requirements = "Notes";
    labels.volume = "Budget Range (USD)";

    origin =
      r?.resolvedLocations?.origin ||
      joinLoc(p.currentCity, p.currentState, p.currentCountry);
    destination =
      r?.resolvedLocations?.destination ||
      joinLoc(p.destCity, p.destState, p.destCountry);
    cargoType = "Business relocation";
    container = relocationScopeLabel(p.relocationScope) || "—";
    incoterm = p.logisticsMode || "—";
    expectedDate = p.preferredMoveDate || "—";
    requirements = p.specialNotes || "—";
    // Reuse the "Volume" slot in the 8-cell grid for budget range (matches request)
    volume = p.budgetRange || "—";
  } else if (kind === "exhibition-logistics") {
    labels.origin = "Origin";
    labels.destination = "Expo venue";
    labels.cargoType = "Materials";
    labels.container = "Packaging / Booth";
    labels.incoterm = "Logistics mode";
    labels.expectedDate = "Expo start date";
    labels.requirements = "Special handling";
    labels.volume = "Urgency level";

    origin = r?.resolvedLocations?.origin || joinLoc(p.orgCity, p.orgState, p.orgCountry);
    destination =
      r?.resolvedLocations?.destination || joinLoc(p.venueCity, p.venueState, p.venueCountry);
    cargoType = p.materialsDescription || "Exhibition materials";
    container = p.packagingType || p.boothNumber || "—";
    incoterm = p.logisticsMode || "—";
    expectedDate = p.exhibitionStartDate || p.setupDate || "—";
    requirements = p.specialHandling || "—";
    // Reuse the "Volume" slot in the 8-cell grid for urgency level
    volume = p.urgencyLevel || "—";
  } else {
    // Generic logistics payload (website form)
    labels.origin = "Origin";
    labels.destination = "Destination";
    labels.cargoType = "Cargo Type";
    labels.container = "Container";
    labels.incoterm = "Incoterms";
    labels.expectedDate = "Expected date";
    labels.requirements = "Preferred payment terms";

    origin = r?.resolvedLocations?.origin || joinLoc(p.originCity, p.originState, p.originCountry);
    destination =
      r?.resolvedLocations?.destination || joinLoc(p.destCity, p.destState, p.destCountry);
    cargoType = p.commodityType || p.cargoDescription || "—";
    container = p.modeDetail || "—";
    incoterm = p.incoterm || "—";
    expectedDate = p.deliveryDate || p.pickupDate || "—";
    requirements = preferredPaymentTerms || p.creditTerms || p.paymentMode || "—";
  }

  return {
    labels,
    origin,
    destination,
    cargoType,
    container,
    weight,
    volume,
    incoterm,
    expectedDate,
    requirements,
  };
}

/** User can act as logistics provider (not only the active role — default active role is buyer/seller first). */
function userHasLogisticsProviderAccess() {
  return getAvailableRoles().some((r) => r.id === "logistics");
}

/** Buyer handover exists — provider can open read-only handover page. */
function canShowViewHandover(row) {
  if (!userHasLogisticsProviderAccess()) return false;
  const hid = Number(row.handoverId);
  if (!Number.isFinite(hid) || hid <= 0) return false;
  const qid = Number(row.quoteId);
  if (!Number.isFinite(qid) || qid <= 0) return false;
  return true;
}

/** Handover submitted = buyer handover row exists for this RFQ’s provider quote (`handover_id` from API). */
function canShowConfirmPickup(row) {
  if (!userHasLogisticsProviderAccess()) return false;
  const hid = Number(row.handoverId);
  if (!Number.isFinite(hid) || hid <= 0) return false;
  const qid = Number(row.quoteId);
  if (!Number.isFinite(qid) || qid <= 0) return false;
  const ps = String(row.handoverPickupStatus || "").trim().toLowerCase();
  if (ps === "picked_up") return false;
  return true;
}

function getReceivedRowActionFlags(r) {
  const viewPath = buildReceivedRfqViewPath(r);
  const ps = String(r.handoverPickupStatus || "").trim().toLowerCase();
  const pickupConfirmed = ps === "picked_up" || ps === "picked up" || ps === "completed";
  const ewayBillId = r.ewayBillId != null ? Number(r.ewayBillId) : null;
  const hasEWayBill = Number.isFinite(ewayBillId) && ewayBillId > 0;
  const shippingManifestId = r.shippingManifestId != null ? Number(r.shippingManifestId) : null;
  const hasManifest = Number.isFinite(shippingManifestId) && shippingManifestId > 0;
  const logisticsGrnId = r.logisticsGrnId != null ? Number(r.logisticsGrnId) : null;
  const hasGrn = Number.isFinite(logisticsGrnId) && logisticsGrnId > 0;
  const quoteSubmitted = isProviderQuoteSubmitted(r.quoteStatus);
  const showInvoiceActions =
    String(r.quoteStatus || "").trim().toLowerCase() === "accepted" &&
    isLogisticsGrnAcceptStageComplete(r);
  const statusPill = getCardStatusPill(r);
  const inTransit =
    pickupConfirmed && hasManifest && r.shippingManifestAccepted && !showInvoiceActions;
  return {
    viewPath,
    pickupConfirmed,
    ewayBillId,
    hasEWayBill,
    shippingManifestId,
    hasManifest,
    logisticsGrnId,
    hasGrn,
    quoteSubmitted,
    showInvoiceActions,
    statusPill,
    inTransit,
  };
}

function ProviderRfqTableActions({ row, flags, onOpenManifest, onOpenGrn, onOpenInvoice }) {
  const r = row;
  const {
    viewPath,
    pickupConfirmed,
    ewayBillId,
    hasEWayBill,
    shippingManifestId,
    hasManifest,
    logisticsGrnId,
    hasGrn,
    quoteSubmitted,
    showInvoiceActions,
    inTransit,
  } = flags;

  const hasWorkflowMenuItems =
    (pickupConfirmed && hasEWayBill) ||
    (pickupConfirmed && hasManifest) ||
    canShowViewHandover(r) ||
    canShowConfirmPickup(r) ||
    (pickupConfirmed && hasManifest && r.shippingManifestAccepted && hasGrn) ||
    showInvoiceActions ||
    inTransit;

  return (
    <div className="lprfq-table__icon-actions">
      <Link
        to={`/dashboard/logistics/rfqs/${r.id}/quote`}
        className="lprfq-table__icon-btn"
        title={quoteSubmitted ? "View quote" : "Submit quote"}
        aria-label={quoteSubmitted ? "View quote" : "Submit quote"}
      >
        {quoteSubmitted ? <FaEye aria-hidden /> : <FaPaperPlane aria-hidden />}
      </Link>
      {viewPath ? (
        <Link
          to={viewPath}
          state={{ from: "logistics-inbox" }}
          className="lprfq-table__icon-btn"
          title="View RFQ"
          aria-label="View RFQ"
        >
          <FaClipboardList aria-hidden />
        </Link>
      ) : null}
      <div className="dropdown lprfq-table__dropdown">
        <button
          type="button"
          className="lprfq-table__icon-btn"
          data-bs-toggle="dropdown"
          data-bs-popper-config='{"strategy":"fixed"}'
          aria-expanded="false"
          aria-label="More actions"
          title="More actions"
        >
          <FaEllipsisV aria-hidden />
        </button>
        <ul className="dropdown-menu dropdown-menu-end shadow-sm">
          {viewPath ? (
            <li>
              <Link className="dropdown-item" to={viewPath} state={{ from: "logistics-inbox" }}>
                View RFQ details
              </Link>
            </li>
          ) : null}
          <li>
            <Link className="dropdown-item" to={`/dashboard/logistics/rfqs/${r.id}/quote`}>
              {quoteSubmitted ? "View quote" : "Submit quote"}
            </Link>
          </li>
          {hasWorkflowMenuItems ? (
            <li>
              <hr className="dropdown-divider" />
            </li>
          ) : null}
          {pickupConfirmed && hasEWayBill ? (
            <li>
              <Link
                className="dropdown-item"
                to="/eway-bill-tracking"
                state={{
                  openEWayBillId: ewayBillId,
                  logisticsSubmissionId: r.id,
                  logisticsQuoteId: r.quoteId,
                  syncDeliveryStageKey: "eway_track",
                }}
              >
                Track E-Way Bill
              </Link>
            </li>
          ) : null}
          {pickupConfirmed && hasManifest ? (
            <li>
              <button type="button" className="dropdown-item" onClick={() => onOpenManifest(shippingManifestId, r)}>
                View Manifest
              </button>
            </li>
          ) : null}
          {canShowViewHandover(r) ? (
            <li>
              <Link className="dropdown-item" to={`/dashboard/logistics/rfqs/${r.id}/handover/${r.quoteId}`}>
                View Handover
              </Link>
            </li>
          ) : null}
          {canShowConfirmPickup(r) ? (
            <li>
              <Link className="dropdown-item" to={`/dashboard/logistics/rfqs/${r.id}/pickup/${r.quoteId}`}>
                Confirm Pickup
              </Link>
            </li>
          ) : null}
          {pickupConfirmed && hasManifest && r.shippingManifestAccepted && hasGrn ? (
            <li>
              <button type="button" className="dropdown-item" onClick={() => onOpenGrn(logisticsGrnId, r)}>
                View GRN
              </button>
            </li>
          ) : null}
          {showInvoiceActions ? (
            <li>
              {Number.isFinite(Number(r.providerInvoiceId)) && Number(r.providerInvoiceId) > 0 ? (
                <button
                  type="button"
                  className="dropdown-item"
                  onClick={() => onOpenInvoice(r.id, r.rfqReferenceNumber)}
                >
                  View Invoice
                </button>
              ) : (
                <Link className="dropdown-item" to={`/dashboard/logistics/rfqs/${r.id}/generate-invoice`}>
                  Generate Invoice
                </Link>
              )}
            </li>
          ) : null}
          {inTransit ? (
            <li>
              <Link
                className="dropdown-item"
                to={`/dashboard/logistics/rfqs/${r.id}/transit/${shippingManifestId}`}
                state={{
                  rfqReferenceNumber: r.rfqReferenceNumber,
                  logisticsSubmissionId: r.id,
                  logisticsQuoteId: r.quoteId,
                }}
              >
                Transit
              </Link>
            </li>
          ) : null}
        </ul>
      </div>
    </div>
  );
}

/**
 * RFQs received by the logged-in logistics provider (`GET /company/logistics-providers/rfq/received`).
 */
export default function LogisticsProviderRfqsPage() {
  const { currencyOptions, loading: currencyLoading } = useCurrencyMaster();
  const { shippingTermsOptions, modeOfTransportOptions } = useFactoryDetailsMaster();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [viewFormat, setViewFormat] = useState(() => {
    try {
      const stored = localStorage.getItem(LPRFQ_VIEW_STORAGE_KEY);
      if (stored === "table") return "table";
      if (stored === "card") return "card";
      return "card";
    } catch {
      return "card";
    }
  });
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteRfq, setQuoteRfq] = useState(null);
  const [quoteResolvedLocations, setQuoteResolvedLocations] = useState(null);
  const [providerInfo, setProviderInfo] = useState(null);
  const [manifestModalOpen, setManifestModalOpen] = useState(false);
  const [manifestLoading, setManifestLoading] = useState(false);
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [manifestModalRfqRow, setManifestModalRfqRow] = useState(null);
  const [grnModalOpen, setGrnModalOpen] = useState(false);
  const [grnModalLoading, setGrnModalLoading] = useState(false);
  const [grnModalData, setGrnModalData] = useState(null);
  const [grnModalRfqRow, setGrnModalRfqRow] = useState(null);
  const [acceptingGrn, setAcceptingGrn] = useState(false);
  const [agrlLogisticsAcceptModalOpen, setAgrlLogisticsAcceptModalOpen] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceModalLoading, setInvoiceModalLoading] = useState(false);
  const [invoiceModalData, setInvoiceModalData] = useState(null);
  const [invoiceModalRfqRef, setInvoiceModalRfqRef] = useState("");
  const [quoteForm, setQuoteForm] = useState(() => ({
    currency: "USD",
    validityDays: 30,
    transitDays: 12,
    quoteExpiryDate: "",
    vesselName: "",
    voyageNumber: "",
    estDeparture: "",
    estArrival: "",
    oceanFreight: "",
    fuelSurcharge: "",
    portHandling: "",
    documentation: "",
    containerDeposit: "",
    // Loading / unloading charges
    loadingCharge: "",
    loadingIncluded: true,
    loadingLocation: "Origin Port",
    unloadingCharge: "",
    unloadingIncluded: true,
    unloadingLocation: "Destination Port",
    // Additional services blocks
    cargoInsuranceOn: false,
    insuranceAmount: "",
    coveragePercent: "",
    coverageType: "Full Cargo Value",
    customsOn: false,
    customsClearanceCharge: "",
    warehousingOn: false,
    freeDaysIncluded: "",
    baseStorageCharge: "",
    perDayChargeAfterFree: "",
    pickupOn: false,
    pickupLocation: "",
    pickupCharge: "",
    deliveryOn: false,
    deliveryLocation: "",
    deliveryCharge: "",
    // Free time / detention / demurrage
    freeDaysOrigin: "",
    freeDaysDestination: "",
    detentionPerDay: "",
    demurragePerDay: "",
    insurance: false,
    customs: false,
    warehouse: false,
    pickup: false,
    paymentTerms: "50% advance, 50% before shipment",
    exclusions: "Customs duties and taxes, Destination port charges, Inland transport",
    additionalNotes: "",
  }));

  const openQuote = (rfq) => {
    setQuoteResolvedLocations(null);
    setProviderInfo(null);
    setQuoteRfq(rfq);
    setQuoteForm((prev) => ({ ...prev })); // preserve defaults
    setQuoteOpen(true);
  };

  const closeQuote = () => {
    setQuoteOpen(false);
    setQuoteRfq(null);
    setQuoteResolvedLocations(null);
    setProviderInfo(null);
  };

  const closeManifestModal = () => {
    setManifestModalOpen(false);
    setSelectedManifest(null);
    setManifestModalRfqRow(null);
  };

  const openGrnModal = async (grnId, rfqRow = null) => {
    const id = Number(grnId);
    if (!Number.isFinite(id) || id <= 0) {
      toast.error("Invalid GRN id");
      return;
    }
    setGrnModalRfqRow(rfqRow && typeof rfqRow === "object" ? rfqRow : null);
    setGrnModalOpen(true);
    setGrnModalLoading(true);
    setGrnModalData(null);
    try {
      const grnResponse = await CommonService.get(`/grn/${id}`);
      if (grnResponse && grnResponse.success && grnResponse.data) {
        setGrnModalData(grnResponse.data);
      } else {
        toast.error(grnResponse?.error || "Failed to load GRN");
        setGrnModalOpen(false);
        setGrnModalRfqRow(null);
      }
    } catch (e) {
      console.error("Error fetching GRN:", e);
      toast.error(e?.response?.data?.error || e?.message || "Failed to load GRN");
      setGrnModalOpen(false);
      setGrnModalRfqRow(null);
    } finally {
      setGrnModalLoading(false);
    }
  };

  const closeGrnModal = () => {
    setGrnModalOpen(false);
    setGrnModalData(null);
    setGrnModalRfqRow(null);
    setGrnModalLoading(false);
    setAgrlLogisticsAcceptModalOpen(false);
  };

  const closeInvoiceModal = () => {
    setInvoiceModalOpen(false);
    setInvoiceModalData(null);
    setInvoiceModalRfqRef("");
    setInvoiceModalLoading(false);
  };

  const openLogisticsInvoiceModal = async (submissionId, rfqRefLabel) => {
    const sid = submissionId != null ? Number(submissionId) : NaN;
    if (!Number.isFinite(sid) || sid <= 0) {
      toast.error("Invalid RFQ");
      return;
    }
    setInvoiceModalOpen(true);
    setInvoiceModalLoading(true);
    setInvoiceModalData(null);
    setInvoiceModalRfqRef(rfqRefLabel != null ? String(rfqRefLabel) : "");
    try {
      const res = await CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_SUBMISSION_INVOICE(sid));
      const pack = res?.data !== undefined ? res.data : res;
      const inner = pack?.data !== undefined ? pack.data : pack;
      const inv = inner?.invoice ?? inner;
      if (!inv || inv.id == null) {
        throw new Error(res?.message || "Invoice not found");
      }
      setInvoiceModalData(inv);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || "Could not load invoice");
      setInvoiceModalOpen(false);
      setInvoiceModalData(null);
    } finally {
      setInvoiceModalLoading(false);
    }
  };

  const handleOpenLogisticsAgrlAcceptModal = () => {
    if (!grnModalData || typeof grnModalData !== "object") {
      toast.error("GRN is still loading");
      return;
    }
    setAgrlLogisticsAcceptModalOpen(true);
  };

  const handleCloseLogisticsAgrlAcceptModal = () => {
    if (acceptingGrn) return;
    setAgrlLogisticsAcceptModalOpen(false);
  };

  const openManifestModal = async (manifestId, rfqRow) => {
    const id = manifestId != null ? Number(manifestId) : NaN;
    if (!Number.isFinite(id) || id <= 0) {
      toast.error("Manifest not found");
      return;
    }
    setManifestModalRfqRow(rfqRow && typeof rfqRow === "object" ? rfqRow : null);
    setManifestModalOpen(true);
    setManifestLoading(true);
    try {
      const res = await CommonService.get(`/shipping-manifest/${id}`);
      const raw = res?.success ? res.data : null;
      if (!raw) throw new Error(res?.message || "Failed to fetch manifest");
      const vm = transformManifestData(raw);
      if (!vm) throw new Error("Failed to map manifest details");
      setSelectedManifest(vm);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to fetch manifest";
      toast.error(msg);
      setManifestModalOpen(false);
      setSelectedManifest(null);
      setManifestModalRfqRow(null);
    } finally {
      setManifestLoading(false);
    }
  };

  const refreshReceivedRfqs = async () => {
    try {
      const listRes = await CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_RECEIVED_FOR_PROVIDER);
      setRows(normalizeRows(listRes));
    } catch (e) {
      console.error("refreshReceivedRfqs failed", e);
      toast.error(e?.response?.data?.message || e?.message || "Could not refresh RFQ list");
    }
  };

  const grnApiResponseSucceeded = (payload) => {
    if (payload == null || typeof payload !== "object") return false;
    if (payload.success === false) return false;
    if (payload.error != null && String(payload.error).trim() !== "") return false;
    return payload.success === true || Boolean(payload.message);
  };

  const buildLogisticsAttestationBody = (form) => ({
    logisticsGrnAttestation: {
      decision: form.decision,
      approverName: form.approverName,
      designation: form.designation,
      approvalDate: form.approvalDate || null,
      approvalNotes: form.approvalNotes || null,
      followUpAction: form.followUpAction && String(form.followUpAction).trim() !== "" ? form.followUpAction : null,
      rejectionReason: form.rejectionReason || null,
    },
  });

  /**
   * Same payloads as `/approve-goods-receipt-list` GRN APIs when the user is allowed to act as seller.
   * For approve / partial, always completes logistics `accept-grn` afterward (with attestation body for audit).
   * If GRN PUT is forbidden (e.g. logistics-only user), still records logistics acceptance for approve/partial.
   */
  const handleSubmitLogisticsGrnFromAgrlModal = async (form) => {
    const grnId = grnModalData?.id != null ? Number(grnModalData.id) : NaN;
    const sid = grnModalRfqRow?.id != null ? Number(grnModalRfqRow.id) : NaN;
    if (!Number.isFinite(grnId) || grnId <= 0) {
      toast.error("GRN id missing");
      return;
    }
    if (!Number.isFinite(sid) || sid <= 0) {
      toast.error("RFQ reference missing");
      return;
    }
    setAcceptingGrn(true);
    try {
      const followUp = form.followUpAction && String(form.followUpAction).trim() !== "" ? form.followUpAction : null;
      const attestationPayload = buildLogisticsAttestationBody(form);

      let grnRes = null;
      let grnStepSkipped = false;

      try {
        if (form.decision === "APPROVE") {
          grnRes = await CommonService.put(`/grn/${grnId}/approve`, {
            approved_by: form.approverName,
            approved_by_designation: form.designation || null,
            approval_notes: form.approvalNotes || null,
            follow_up_action: followUp,
          });
        } else if (form.decision === "PARTIALLY_ACCEPT") {
          grnRes = await CommonService.put(`/grn/${grnId}/partially-approve`, {
            approved_by: form.approverName,
            approved_by_designation: form.designation || null,
            approval_notes: form.approvalNotes || null,
            follow_up_action: followUp,
          });
        } else {
          grnRes = await CommonService.put(`/grn/${grnId}/reject`, {
            rejected_by: form.approverName,
            rejected_by_designation: form.designation || null,
            rejection_reason: form.rejectionReason,
            follow_up_action: followUp,
          });
        }
      } catch (e) {
        const status = e?.response?.status;
        const errMsg =
          e?.response?.data?.error || e?.response?.data?.message || e?.message || "Could not save GRN decision";
        const allowLogisticsOnly =
          (form.decision === "APPROVE" || form.decision === "PARTIALLY_ACCEPT") &&
          (status === 401 || status === 403 || status === 404);
        if (allowLogisticsOnly) {
          toast.info(`${errMsg} Recording logistics acceptance for this shipment step.`);
          grnRes = { success: true, message: "Logistics acceptance" };
          grnStepSkipped = true;
        } else {
          toast.error(errMsg);
          return;
        }
      }

      if (!grnApiResponseSucceeded(grnRes)) {
        toast.error(grnRes?.error || grnRes?.message || "Could not save GRN decision");
        return;
      }

      const grnMsg = grnRes.message || (grnStepSkipped ? "Logistics acceptance recorded" : "GRN decision saved");

      if (form.decision === "REJECT") {
        toast.success(grnMsg);
        setAgrlLogisticsAcceptModalOpen(false);
        closeGrnModal();
        await refreshReceivedRfqs();
        return;
      }

      const logisticsRes = await CommonService.post(
        API_URLS.LOGISTICS_PROVIDER.RFQ_RECEIVED_ACCEPT_GRN(sid),
        attestationPayload,
      );
      if (logisticsRes && logisticsRes.success !== false && !logisticsRes.error) {
        toast.success(`${grnMsg} Shipment workflow updated.`);
        setAgrlLogisticsAcceptModalOpen(false);
        closeGrnModal();
        await refreshReceivedRfqs();
      } else {
        toast.error(
          logisticsRes?.message ||
            logisticsRes?.error ||
            "GRN decision saved, but logistics shipment step could not be updated.",
        );
      }
    } catch (e) {
      const msg =
        e?.response?.data?.error || e?.response?.data?.message || e?.message || "Could not complete submission";
      toast.error(msg);
    } finally {
      setAcceptingGrn(false);
    }
  };

  const totalQuoteAmount =
    toNum(quoteForm.oceanFreight) +
    toNum(quoteForm.fuelSurcharge) +
    toNum(quoteForm.portHandling) +
    toNum(quoteForm.documentation) +
    toNum(quoteForm.containerDeposit) +
    toNum(quoteForm.loadingCharge) +
    toNum(quoteForm.unloadingCharge) +
    toNum(quoteForm.customsClearanceCharge) +
    toNum(quoteForm.baseStorageCharge) +
    toNum(quoteForm.perDayChargeAfterFree) +
    toNum(quoteForm.pickupCharge) +
    toNum(quoteForm.deliveryCharge);

  const currencySelectOptions =
    Array.isArray(currencyOptions) && currencyOptions.length
      ? currencyOptions
          .map((o) => ({
            value: (o.currencyCode || o.value) != null ? String(o.currencyCode || o.value) : "",
            label: o.label,
          }))
          .filter((o) => o.value && o.label)
      : [
          { value: "USD", label: "USD ($)" },
          { value: "INR", label: "INR (₹)" },
          { value: "EUR", label: "EUR (€)" },
        ];

  useEffect(() => {
    let cancelled = false;
    if (!quoteOpen || !quoteRfq?.payload) return undefined;
    const p = quoteRfq.payload;
    const kind = quoteRfq?.rfqKind != null ? String(quoteRfq.rfqKind).trim().toLowerCase() : "generic";

    const pick = () => {
      if (kind === "business-relocation") {
        return {
          origin: { c: p.currentCountry, s: p.currentState, cy: p.currentCity },
          destination: { c: p.destCountry, s: p.destState, cy: p.destCity },
        };
      }
      if (kind === "exhibition-logistics") {
        return {
          origin: { c: p.orgCountry, s: p.orgState, cy: p.orgCity },
          destination: { c: p.venueCountry, s: p.venueState, cy: p.venueCity },
        };
      }
      return {
        origin: { c: p.originCountry, s: p.originState, cy: p.originCity },
        destination: { c: p.destCountry, s: p.destState, cy: p.destCity },
      };
    };

    (async () => {
      const sel = pick();
      const [o, d] = await Promise.all([
        resolveLocationLabels(sel.origin.c, sel.origin.s, sel.origin.cy),
        resolveLocationLabels(sel.destination.c, sel.destination.s, sel.destination.cy),
      ]);
      if (cancelled) return;
      const join = (x) => [x.city, x.state, x.country].filter(Boolean).join(", ") || "—";
      setQuoteResolvedLocations({ origin: join(o), destination: join(d) });
    })();

    return () => {
      cancelled = true;
    };
  }, [quoteOpen, quoteRfq?.id]);

  useEffect(() => {
    let cancelled = false;
    if (!quoteOpen) return undefined;
    (async () => {
      try {
        const info = await fetchLogisticsProviderDisplayInfo(CommonService);
        if (!cancelled) setProviderInfo(info);
      } catch {
        if (!cancelled) setProviderInfo(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [quoteOpen]);

  const loadReceivedRfqs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_RECEIVED_FOR_PROVIDER);
      setRows(normalizeRows(res));
    } catch (e) {
      const errorMessage =
        e?.response?.data?.message || e?.message || "Could not load RFQs for your company.";
      toast.error(errorMessage);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReceivedRfqs();
  }, [loadReceivedRfqs]);

  const stats = useMemo(() => computeStats(rows), [rows]);

  const filteredRows = useMemo(
    () => filterRows(rows, { search: searchQuery, statusFilter, kindFilter, dateFrom, dateTo }),
    [rows, searchQuery, statusFilter, kindFilter, dateFrom, dateTo],
  );

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginatedRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, safePage]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter, kindFilter, dateFrom, dateTo]);

  useEffect(() => {
    try {
      localStorage.setItem(LPRFQ_VIEW_STORAGE_KEY, viewFormat);
    } catch {
      /* ignore */
    }
  }, [viewFormat]);

  const resetFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setKindFilter("all");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  return (
    <div className="container-fluid py-4 lprfq-page">
      <nav aria-label="breadcrumb" className="lprfq-breadcrumb">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item">
            <Link to="/dashboard/logistics">Dashboard</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            RFQs Received
          </li>
        </ol>
      </nav>

      <div className="lprfq-hero">
        <div className="lprfq-hero__copy">
          <h1 className="lprfq-hero__title">RFQs Received</h1>
          <p className="lprfq-hero__subtitle">
            Incoming logistics RFQs where your company is the selected provider.
          </p>
        </div>
        <div className="lprfq-hero__art" aria-hidden>
          <div className="lprfq-hero__clipboard" />
          <div className="lprfq-hero__truck" />
        </div>
      </div>

      <div className="lprfq-stats">
        <div className="lprfq-stat-card lprfq-stat-card--total">
          <div className="lprfq-stat-card__icon">
            <FaClipboardList aria-hidden />
          </div>
          <div>
            <div className="lprfq-stat-card__value">{loading ? "…" : stats.total}</div>
            <div className="lprfq-stat-card__label">Total RFQs</div>
            <div className="lprfq-stat-card__hint">All time</div>
          </div>
        </div>
        <div className="lprfq-stat-card lprfq-stat-card--pending">
          <div className="lprfq-stat-card__icon">
            <FaClock aria-hidden />
          </div>
          <div>
            <div className="lprfq-stat-card__value">{loading ? "…" : stats.pendingQuote}</div>
            <div className="lprfq-stat-card__label">Pending Quote</div>
            <div className="lprfq-stat-card__hint">Awaiting your quote</div>
          </div>
        </div>
        <div className="lprfq-stat-card lprfq-stat-card--picked">
          <div className="lprfq-stat-card__icon">
            <FaTruckLoading aria-hidden />
          </div>
          <div>
            <div className="lprfq-stat-card__value">{loading ? "…" : stats.pickedUp}</div>
            <div className="lprfq-stat-card__label">Picked Up</div>
            <div className="lprfq-stat-card__hint">In progress</div>
          </div>
        </div>
        <div className="lprfq-stat-card lprfq-stat-card--done">
          <div className="lprfq-stat-card__icon">
            <FaCheckCircle aria-hidden />
          </div>
          <div>
            <div className="lprfq-stat-card__value">{loading ? "…" : stats.completed}</div>
            <div className="lprfq-stat-card__label">Completed</div>
            <div className="lprfq-stat-card__hint">Successfully completed</div>
          </div>
        </div>
      </div>

      <div className="lprfq-filters">
        <div className="lprfq-filters__search">
          <FaSearch className="lprfq-filters__search-icon" aria-hidden />
          <input
            type="search"
            className="form-control"
            placeholder="Search RFQ ID, contact, industry…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search RFQs"
          />
        </div>
        <select
          className="form-select lprfq-filters__select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
        >
          <option value="all">All Status</option>
          <option value="pending_quote">Pending Quote</option>
          <option value="quoted">Quoted</option>
          <option value="picked_up">Picked Up</option>
          <option value="completed">Completed</option>
        </select>
        <select
          className="form-select lprfq-filters__select"
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          aria-label="Filter by logistics type"
        >
          <option value="all">All Types</option>
          <option value="generic">Generic Logistics</option>
          <option value="business-relocation">Business Relocation</option>
          <option value="exhibition-logistics">Exhibition Logistics</option>
        </select>
        <input
          type="date"
          className="form-control lprfq-filters__date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          aria-label="From date"
          title="From date"
        />
        <input
          type="date"
          className="form-control lprfq-filters__date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          aria-label="To date"
          title="To date"
        />
        <button type="button" className="lprfq-filters__reset" onClick={resetFilters}>
          <FaRedo aria-hidden />
          Reset
        </button>
      </div>

      {loading ? (
        <div className="lprfq-loading">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="lprfq-empty">
          <FaInbox className="lprfq-empty__icon" aria-hidden />
          <h5 className="text-secondary fw-semibold">
            {rows.length === 0 ? "No RFQs yet" : "No RFQs match your filters"}
          </h5>
          <p className="text-muted mb-3">
            {rows.length === 0
              ? "Buyers will appear here when they submit an RFQ to your company."
              : "Try adjusting search or filter criteria."}
          </p>
          {rows.length > 0 ? (
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={resetFilters}>
              Clear filters
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <div className="lprfq-view-toolbar">
            <span className="lprfq-view-toolbar__meta">
              {filteredRows.length} RFQ{filteredRows.length === 1 ? "" : "s"} • Page {safePage} of {totalPages}{" "}
              (up to {PAGE_SIZE} per page)
            </span>
            <div className="lprfq-view-toggle" role="group" aria-label="Display format">
              <button
                type="button"
                className={`lprfq-view-toggle__btn ${viewFormat === "card" ? "lprfq-view-toggle__btn--active" : ""}`}
                onClick={() => setViewFormat("card")}
                aria-pressed={viewFormat === "card"}
                aria-label="Card view"
                title="Card view"
              >
                <FaThLarge aria-hidden />
              </button>
              <button
                type="button"
                className={`lprfq-view-toggle__btn ${viewFormat === "table" ? "lprfq-view-toggle__btn--active" : ""}`}
                onClick={() => setViewFormat("table")}
                aria-pressed={viewFormat === "table"}
                aria-label="Table view"
                title="Table view"
              >
                <FaTable aria-hidden />
              </button>
            </div>
          </div>

          {viewFormat === "table" ? (
            <div className="lprfq-table-wrap">
              <table className="lprfq-table">
                <thead>
                  <tr>
                    <th scope="col">RFQ reference</th>
                    <th scope="col">Type</th>
                    <th scope="col">Contact</th>
                    <th scope="col">Details</th>
                    <th scope="col">Submitted</th>
                    <th scope="col">Status</th>
                    <th scope="col" className="lprfq-table__col-actions">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.map((r) => {
                    const flags = getReceivedRowActionFlags(r);
                    const kindVisual = getKindTableVisual(r.rfqKind);
                    const KindIcon = kindVisual.Icon;
                    const contactLabel =
                      (r.listSource === "business_relocation_rfq" ||
                        r.listSource === "exhibition_logistics_rfq") &&
                      r.allocator
                        ? r.allocator.name || "—"
                        : r.contactPerson || "—";

                    return (
                      <tr key={`rfq-table-${receivedRowKey(r)}`}>
                        <td>
                          <div className="lprfq-table__ref-cell" title={r.rfqReferenceNumber}>
                            <span
                              className={`lprfq-table__kind-icon lprfq-table__kind-icon--${kindVisual.tone}`}
                            >
                              <KindIcon aria-hidden />
                            </span>
                            <span className="lprfq-table__ref-text">{r.rfqReferenceNumber}</span>
                          </div>
                        </td>
                        <td>
                          <div className="lprfq-table__type-cell">
                            <span
                              className={`lprfq-table__kind-icon lprfq-table__kind-icon--sm lprfq-table__kind-icon--${kindVisual.tone}`}
                            >
                              <KindIcon aria-hidden />
                            </span>
                            <span className={`lprfq-table__type-label lprfq-table__type-label--${kindVisual.tone}`}>
                              {rfqKindLabel(r.rfqKind)}
                            </span>
                          </div>
                        </td>
                        <td>{contactLabel}</td>
                        <td className="lprfq-table__details">{receivedRowDetailSummary(r)}</td>
                        <td>{formatDate(r.submittedAt)}</td>
                        <td>
                          <span className={flags.statusPill.className}>{flags.statusPill.label}</span>
                        </td>
                        <td className="lprfq-table__actions">
                          <ProviderRfqTableActions
                            row={r}
                            flags={flags}
                            onOpenManifest={openManifestModal}
                            onOpenGrn={openGrnModal}
                            onOpenInvoice={openLogisticsInvoiceModal}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="lprfq-grid">
              {paginatedRows.map((r) => {
            const flags = getReceivedRowActionFlags(r);
            const {
              viewPath,
              pickupConfirmed,
              ewayBillId,
              hasEWayBill,
              shippingManifestId,
              hasManifest,
              logisticsGrnId,
              hasGrn,
              quoteSubmitted,
              showInvoiceActions,
              statusPill,
              inTransit,
            } = flags;

            return (
              <article key={`rfq-card-${receivedRowKey(r)}`} className="lprfq-card">
                <div className="lprfq-card__top">
                  <div className="lprfq-card__top-left">
                    <span className="lprfq-card__ref" title={r.rfqReferenceNumber}>
                      {truncateRef(r.rfqReferenceNumber, 16)}
                    </span>
                    <span className={statusPill.className}>{statusPill.label}</span>
                  </div>
                  <div className="dropdown lprfq-table__dropdown">
                    <button
                      type="button"
                      className="lprfq-card__menu"
                      data-bs-toggle="dropdown"
                      data-bs-popper-config='{"strategy":"fixed"}'
                      aria-expanded="false"
                      aria-label="RFQ options"
                    >
                      <FaEllipsisV aria-hidden />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end shadow-sm">
                      {viewPath ? (
                        <li>
                          <Link className="dropdown-item" to={viewPath} state={{ from: "logistics-inbox" }}>
                            View RFQ details
                          </Link>
                        </li>
                      ) : null}
                      <li>
                        <Link className="dropdown-item" to={`/dashboard/logistics/rfqs/${r.id}/quote`}>
                          {quoteSubmitted ? "View quote" : "Submit quote"}
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>

                <h3 className={getKindTitleClass(r.rfqKind)}>{rfqKindLabel(r.rfqKind)}</h3>

                <div className="lprfq-card__details">
                  {(r.listSource === "business_relocation_rfq" ||
                    r.listSource === "exhibition_logistics_rfq") &&
                  r.allocator ? (
                    <>
                      <RfqMetaRow
                        label="Contact"
                        value={`${r.allocator.name || "—"}${r.allocator.roleLabel ? ` (${r.allocator.roleLabel})` : ""}`}
                      >
                        <FaUser />
                      </RfqMetaRow>
                      {r.rfqKind === "business-relocation" ? (
                        <>
                          <RfqMetaRow label="Logistics mode" value={resolveRowLogisticsMode(r)}>
                            <FaTruck />
                          </RfqMetaRow>
                          <RfqMetaRow label="Budget range" value={resolveRowBudgetRange(r)}>
                            <FaWallet />
                          </RfqMetaRow>
                        </>
                      ) : (
                        <RfqMetaRow label="Logistics mode" value={resolveRowLogisticsMode(r)}>
                          <FaTruck />
                        </RfqMetaRow>
                      )}
                    </>
                  ) : (
                    <RfqMetaRow label="Contact" value={r.contactPerson}>
                      <FaUser />
                    </RfqMetaRow>
                  )}
                  {r.rfqKind === "generic" ? (
                    <>
                      <RfqMetaRow label="Industry" value={r.industry}>
                        <FaIndustry />
                      </RfqMetaRow>
                      <RfqMetaRow label="Transport" value={transportModeLabel(r.transportMode)}>
                        <FaTruck />
                      </RfqMetaRow>
                    </>
                  ) : null}
                  {r.rfqKind === "business-relocation" ? (
                    <RfqMetaRow label="Relocation" value={relocationScopeLabel(r.relocationScope)}>
                      <FaBuilding />
                    </RfqMetaRow>
                  ) : null}
                  {r.rfqKind === "exhibition-logistics" ? (
                    <>
                      <RfqMetaRow label="Exhibition" value={r.exhibitionName}>
                        <FaStar />
                      </RfqMetaRow>
                      <RfqMetaRow
                        label="Expo dates"
                        value={`${formatExpoDate(r.exhibitionStartDate)} – ${formatExpoDate(r.exhibitionEndDate)}`}
                      >
                        <FaCalendarAlt />
                      </RfqMetaRow>
                    </>
                  ) : null}
                  <RfqMetaRow label="Submitted on" value={formatDate(r.submittedAt)}>
                    <FaCalendarCheck />
                  </RfqMetaRow>
                </div>

                <div className="lprfq-card__actions">
                  <Link
                    to={`/dashboard/logistics/rfqs/${r.id}/quote`}
                    className={`lprfq-btn lprfq-btn--block ${quoteSubmitted ? "lprfq-btn--success" : "lprfq-btn--primary"}`}
                  >
                    {quoteSubmitted ? <FaEye aria-hidden /> : <FaPaperPlane aria-hidden />}
                    {quoteSubmitted ? "View Quote" : "Submit Quote"}
                  </Link>

                  <div className="lprfq-card__actions-secondary">
                    {viewPath ? (
                      <Link
                        to={viewPath}
                        state={{ from: "logistics-inbox" }}
                        className="lprfq-btn lprfq-btn--outline-sm"
                      >
                        View RFQ
                      </Link>
                    ) : null}
                    {pickupConfirmed && hasEWayBill ? (
                      <Link
                        to="/eway-bill-tracking"
                        state={{
                          openEWayBillId: ewayBillId,
                          logisticsSubmissionId: r.id,
                          logisticsQuoteId: r.quoteId,
                          syncDeliveryStageKey: "eway_track",
                        }}
                        className="lprfq-btn lprfq-btn--outline-sm"
                      >
                        Track E-Way Bill
                      </Link>
                    ) : null}
                    {pickupConfirmed && hasManifest ? (
                      <button
                        type="button"
                        onClick={() => openManifestModal(shippingManifestId, r)}
                        className="lprfq-btn lprfq-btn--outline-sm"
                      >
                        View Manifest
                      </button>
                    ) : null}
                    {canShowViewHandover(r) ? (
                      <Link
                        to={`/dashboard/logistics/rfqs/${r.id}/handover/${r.quoteId}`}
                        className="lprfq-btn lprfq-btn--outline-sm"
                      >
                        View Handover
                      </Link>
                    ) : null}
                    {canShowConfirmPickup(r) ? (
                      <Link
                        to={`/dashboard/logistics/rfqs/${r.id}/pickup/${r.quoteId}`}
                        className="lprfq-btn lprfq-btn--outline-sm lprfq-btn--outline-sm-primary"
                      >
                        Confirm Pickup
                      </Link>
                    ) : null}
                    {pickupConfirmed && hasManifest && r.shippingManifestAccepted && hasGrn ? (
                      <button
                        type="button"
                        className="lprfq-btn lprfq-btn--outline-sm"
                        onClick={() => openGrnModal(logisticsGrnId, r)}
                      >
                        View GRN
                      </button>
                    ) : null}
                    {showInvoiceActions ? (
                      Number.isFinite(Number(r.providerInvoiceId)) && Number(r.providerInvoiceId) > 0 ? (
                        <button
                          type="button"
                          className="lprfq-btn lprfq-btn--outline-sm lprfq-btn--outline-sm-invoice"
                          onClick={() => openLogisticsInvoiceModal(r.id, r.rfqReferenceNumber)}
                        >
                          View Invoice
                        </button>
                      ) : (
                        <Link
                          to={`/dashboard/logistics/rfqs/${r.id}/generate-invoice`}
                          className="lprfq-btn lprfq-btn--outline-sm lprfq-btn--outline-sm-invoice"
                        >
                          Generate Invoice
                        </Link>
                      )
                    ) : null}
                    {inTransit ? (
                      <Link
                        to={`/dashboard/logistics/rfqs/${r.id}/transit/${shippingManifestId}`}
                        state={{
                          rfqReferenceNumber: r.rfqReferenceNumber,
                          logisticsSubmissionId: r.id,
                          logisticsQuoteId: r.quoteId,
                        }}
                        className="lprfq-btn lprfq-btn--outline-sm lprfq-btn--outline-sm-primary lprfq-btn--grid-span"
                      >
                        <FaRoute aria-hidden />
                        Transit
                      </Link>
                    ) : null}
                  </div>
                </div>
              </article>
            );
          })}
            </div>
          )}

          <div className="lprfq-pagination">
            <span>
              Showing {(safePage - 1) * PAGE_SIZE + 1} to {Math.min(safePage * PAGE_SIZE, filteredRows.length)} of{" "}
              {filteredRows.length} RFQ{filteredRows.length === 1 ? "" : "s"}
            </span>
            <div className="lprfq-pagination__controls">
              <button
                type="button"
                className="lprfq-pagination__btn"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .slice(Math.max(0, safePage - 3), safePage + 2)
                .map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`lprfq-pagination__btn ${n === safePage ? "lprfq-pagination__btn--active" : ""}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                ))}
              <button
                type="button"
                className="lprfq-pagination__btn"
                disabled={safePage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      <ShippingManifestDetailsModal
        open={Boolean(manifestModalOpen && selectedManifest && !manifestLoading)}
        manifest={selectedManifest}
        onClose={closeManifestModal}
        shippingTermsOptions={shippingTermsOptions}
        modeOfTransportOptions={modeOfTransportOptions}
      />

      <LogisticsGrnDetailsModal
        open={grnModalOpen}
        onClose={closeGrnModal}
        grn={grnModalData}
        loading={grnModalLoading}
        logisticsShowAcceptGrn={shouldShowLogisticsAcceptGrnButton(grnModalRfqRow, grnModalData)}
        logisticsAcceptGrnLoading={acceptingGrn || agrlLogisticsAcceptModalOpen}
        onLogisticsAcceptGrn={handleOpenLogisticsAgrlAcceptModal}
      />

      <LogisticsGrnAgrlAcceptModal
        open={agrlLogisticsAcceptModalOpen && Boolean(grnModalData)}
        grn={grnModalData}
        onClose={handleCloseLogisticsAgrlAcceptModal}
        onSubmitDecision={handleSubmitLogisticsGrnFromAgrlModal}
        submitting={acceptingGrn}
      />

      {invoiceModalOpen ? (
        <div className="grn-manifest-list-modal-wrapper">
          <div className="grn-generate">
            <div className="grn-preview-modal-overlay" onClick={closeInvoiceModal} role="presentation">
              <div
                className="grn-preview-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="logistics-invoice-modal-title"
                onClick={(e) => e.stopPropagation()}
                tabIndex={-1}
              >
                <button
                  type="button"
                  className="grn-preview-modal-close"
                  onClick={closeInvoiceModal}
                  aria-label="Close invoice details"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
                <div className="grn-preview-modal-header">
                  <div>
                    <h2 id="logistics-invoice-modal-title" className="grn-preview-modal-title">
                      Logistics invoice
                      {invoiceModalRfqRef ? (
                        <span className="text-muted fw-normal ms-1 small font-monospace">{invoiceModalRfqRef}</span>
                      ) : null}
                    </h2>
                    <p className="grn-preview-modal-subtitle">Saved invoice for this RFQ submission.</p>
                  </div>
                </div>
                <div className="grn-preview-content">
                  {invoiceModalLoading ? (
                    <div className="p-4 text-center text-muted">Loading invoice…</div>
                  ) : !invoiceModalData ? (
                    <div className="p-4 text-center text-muted">No invoice data.</div>
                  ) : (
                    <div className="px-2 pb-3">
                      <div className="row g-2 small mb-3">
                        <div className="col-md-4">
                          <strong>Invoice #</strong>
                          <div className="font-monospace">{invoiceModalData.invoiceNumber ?? "—"}</div>
                        </div>
                        <div className="col-md-4">
                          <strong>Date</strong>
                          <div>{invoiceModalData.invoiceDate ?? "—"}</div>
                        </div>
                        <div className="col-md-4">
                          <strong>Due</strong>
                          <div>{invoiceModalData.dueDate ?? "—"}</div>
                        </div>
                        <div className="col-md-4">
                          <strong>RFQ ref</strong>
                          <div>{invoiceModalData.rfqReference ?? "—"}</div>
                        </div>
                        <div className="col-md-4">
                          <strong>Currency</strong>
                          <div>{invoiceModalData.currencyCode ?? "—"}</div>
                        </div>
                        <div className="col-md-4">
                          <strong>Status</strong>
                          <div>{invoiceModalData.status ?? "—"}</div>
                        </div>
                      </div>
                      <div className="row g-3 small mb-3">
                        <div className="col-md-6">
                          <h6 className="text-secondary">Provider</h6>
                          <div>{invoiceModalData.provider?.companyName ?? "—"}</div>
                          <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
                            {invoiceModalData.provider?.address || ""}
                          </div>
                          <div className="mt-1">GSTIN: {invoiceModalData.provider?.gstin ?? "—"}</div>
                        </div>
                        <div className="col-md-6">
                          <h6 className="text-secondary">Buyer</h6>
                          <div>{invoiceModalData.buyer?.companyName ?? "—"}</div>
                          <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
                            {invoiceModalData.buyer?.address || ""}
                          </div>
                          <div className="mt-1">Tax ID: {invoiceModalData.buyer?.taxId ?? "—"}</div>
                        </div>
                      </div>
                      <table className="table table-sm table-bordered mb-2">
                        <tbody>
                          <tr>
                            <th>Subtotal</th>
                            <td className="text-end">{Number(invoiceModalData.subtotal ?? 0).toFixed(2)}</td>
                          </tr>
                          <tr>
                            <th>Tax ({Number(invoiceModalData.taxPercent ?? 0).toFixed(2)}%)</th>
                            <td className="text-end">{Number(invoiceModalData.taxAmount ?? 0).toFixed(2)}</td>
                          </tr>
                          <tr className="table-light fw-bold">
                            <th>Grand total</th>
                            <td className="text-end">{Number(invoiceModalData.grandTotal ?? 0).toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                      {invoiceModalData.notes ? (
                        <div className="small text-muted mb-0">
                          <strong>Notes</strong>
                          <div style={{ whiteSpace: "pre-wrap" }}>{invoiceModalData.notes}</div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
                <div className="grn-preview-modal-footer d-flex justify-content-end">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={closeInvoiceModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
